import Box from "@mui/material/Box";
import CardMedia from "@mui/material/CardMedia";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";

import { useGetProductQuery } from "services/products";

// The picture URL is a presigned S3 URL that expires after ~1h. Refetch the
// product when the cached entry is older than 55 minutes so the <img> never
// points at an expired link.
const PICTURE_URL_MAX_AGE_SECONDS = 55 * 60;

type ProductPictureDialogProps = {
  productId: number;
  open: boolean;
  onClose: () => void;
};

// Fetches the product (and with it the presigned picture URL) only when the
// dialog is open — list surfaces carry just the cheap `has_picture` boolean, so
// nothing image-related is loaded until the user actually asks for it.
export default function ProductPictureDialog({
  productId,
  open,
  onClose
}: ProductPictureDialogProps) {
  const {
    data: product,
    isFetching,
    isError
  } = useGetProductQuery(
    { id: productId },
    { skip: !open, refetchOnMountOrArgChange: PICTURE_URL_MAX_AGE_SECONDS }
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        sx={{ p: 4, minWidth: 300, minHeight: 200 }}
      >
        {isFetching ? (
          <CircularProgress data-testid="product-picture-loading" />
        ) : product?.picture ? (
          <CardMedia
            sx={{ maxHeight: "calc(100dvh - 200px)" }}
            component="img"
            alt="Product picture"
            height="auto"
            image={product.picture}
          />
        ) : (
          <Typography>
            {isError ? "Failed to load picture" : "No image to display"}
          </Typography>
        )}
      </Box>
    </Dialog>
  );
}
